import * as React from "react"
import { router } from "@inertiajs/react"
import { Copy, Plus, Radio, Trash2 } from "lucide-react"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Checkbox } from "@ui/Checkbox"
import { Input } from "@ui/Input"
import { Pill } from "@ui/Pill"
import { Select } from "@ui/Select"
import { Switch } from "@ui/Switch"
import { toast } from "sonner"
import { cn } from "@lib/cn"
import type { ApiRequest, Environment, KeyValueEntry, Mock, MockCondition, Project } from "@lib/inertia-types"
import { CodeEditor } from "./CodeEditor"
import { KeyValueEditor } from "./KeyValueEditor"

const CONDITION_SOURCES = ["query", "header", "path_param", "body_json", "state"] as const
const CONDITION_OPERATORS = ["eq", "contains", "regex", "exists"] as const

const BODY_MODES = [
  { value: "none", label: "No body" },
  { value: "json", label: "JSON" },
  { value: "xml", label: "XML" },
  { value: "text", label: "Text" },
] as const

const BODY_PLACEHOLDERS: Record<"json" | "xml" | "text", string> = {
  json: '{"status": "ok", "id": "{{ params.id }}", "ref": "{{ "" | uuid }}"}',
  xml: '<response>\n  <status>ok</status>\n  <id>{{ params.id }}</id>\n</response>',
  text: "Plain text response…",
}

// The default Content-Type the responder emits for each body format (mirrors
// MockServing::Responder#guess_content_type).
const CONTENT_TYPE_FOR: Record<"json" | "xml" | "text", string> = {
  json: "application/json",
  xml: "application/xml",
  text: "text/plain",
}

// The mock's response format for editor highlighting. Content-Type is the
// source of truth; fall back to sniffing the first non-space character.
function inferBodyFormat(mock: Mock): "json" | "xml" | "text" {
  const contentType = Object.entries(mock.response_headers ?? {})
    .find(([k]) => k.toLowerCase() === "content-type")?.[1]
    ?.toLowerCase()
  if (contentType) {
    if (/xml|html/.test(contentType)) return "xml"
    if (/json/.test(contentType)) return "json"
    return "text"
  }
  const head = (mock.response_body ?? "").trimStart()
  if (head.startsWith("<")) return "xml"
  if (head.startsWith("{") || head.startsWith("[")) return "json"
  return "json"
}

export function MocksTab({
  project,
  request,
  environments,
  serviceSlug,
  environmentId = null,
  mockToken,
  host,
}: {
  project: Project
  request: ApiRequest
  environments: Environment[]
  serviceSlug: string | null
  environmentId?: number | null
  mockToken?: string
  host: string
}) {
  const mocks = request.mocks ?? []
  const [selectedId, setSelectedId] = React.useState<number | null>(mocks[0]?.id ?? null)

  // When a variant newly appears (captured from a run, or the "+" button),
  // jump the selection to it so the user lands on what they just created.
  const knownIds = React.useRef<number[]>(mocks.map((m) => m.id))
  React.useEffect(() => {
    const added = mocks.filter((m) => !knownIds.current.includes(m.id))
    if (added.length > 0) setSelectedId(added[added.length - 1].id)
    knownIds.current = mocks.map((m) => m.id)
  }, [mocks])

  const selected = mocks.find((m) => m.id === selectedId) ?? null

  // Mirror the server's selection: for the currently-selected environment,
  // the effective default is the enabled, unconditioned, applicable variant
  // with the lowest (priority, id). Any others are shadowed (never fire).
  const isApplicable = (m: Mock) => m.enabled && (m.environment_id === null || m.environment_id === environmentId)
  const isUnconditioned = (m: Mock) => (m.conditions?.length ?? 0) === 0
  const defaultCandidates = mocks
    .filter((m) => isApplicable(m) && isUnconditioned(m))
    .slice()
    .sort((a, b) => a.priority - b.priority || a.id - b.id)
  const defaultMock = defaultCandidates[0] ?? null
  const shadowedIds = new Set(defaultCandidates.slice(1).map((m) => m.id))

  function addMock() {
    router.post(
      `/projects/${project.slug}/requests/${request.id}/mocks`,
      { name: mocks.length === 0 ? "Happy path" : "Variant", response_body: "{\n  \n}" },
      { preserveScroll: true, only: ["selected_request"] },
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <MockUrlHint
        project={project}
        request={request}
        environments={environments}
        serviceSlug={serviceSlug}
        environmentId={environmentId}
        mockToken={mockToken}
        host={host}
      />
      <div className="flex min-h-0 flex-1 gap-3">
        <div className="flex w-56 shrink-0 flex-col gap-2">
          <div className="flex items-center justify-between">
          <span className="text-mini font-semibold uppercase tracking-wide text-cloverhound-gray-600">
            Variants
          </span>
          <Button variant="ghost" size="iconXs" onClick={addMock} aria-label="Add mock variant">
            <Plus className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
        {mocks.length === 0 ? (
          <p className="text-mini text-cloverhound-gray-600">
            No mocks yet. Add a variant to serve this request from the public mock endpoint.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              {mocks.map((mock) => {
                const conditioned = !isUnconditioned(mock)
                const isDefault = mock.id === defaultMock?.id
                const isShadowed = shadowedIds.has(mock.id)
                return (
                  <button
                    key={mock.id}
                    type="button"
                    onClick={() => setSelectedId(mock.id)}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-mini transition-colors",
                      mock.id === selectedId
                        ? "bg-cloverhound-gray-100 dark:bg-cloverhound-gray-850"
                        : "hover:bg-cloverhound-gray-100 dark:hover:bg-cloverhound-gray-850",
                    )}
                  >
                    <Radio
                      className={cn(
                        "size-3.5 shrink-0",
                        mock.enabled ? "text-cloverhound-green-500" : "text-cloverhound-gray-400",
                      )}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate font-medium text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
                      {mock.name}
                    </span>
                    {isDefault && (
                      <span className="shrink-0 rounded-pill bg-cloverhound-indigo-50 px-1.5 text-micro font-medium text-cloverhound-indigo-700 dark:bg-cloverhound-indigo-900/40 dark:text-cloverhound-indigo-300">
                        default
                      </span>
                    )}
                    {conditioned && (
                      <span className="shrink-0 text-micro text-cloverhound-gray-500">if</span>
                    )}
                    {isShadowed && (
                      <span className="shrink-0 rounded-pill bg-cloverhound-yellow-50 px-1.5 text-micro font-medium text-cloverhound-yellow-700 dark:bg-cloverhound-yellow-500/10 dark:text-cloverhound-yellow-400" title="Never fires — another unconditioned variant is the default">
                        unused
                      </span>
                    )}
                    <span className="shrink-0 font-mono text-micro text-cloverhound-gray-500">{mock.response_status}</span>
                  </button>
                )
              })}
            </div>
            {shadowedIds.size > 0 && (
              <p className="rounded-md bg-cloverhound-yellow-50 px-2 py-1.5 text-micro text-cloverhound-yellow-700 dark:bg-cloverhound-yellow-500/10 dark:text-cloverhound-yellow-400">
                Only one unconditioned variant can be the default
                {environmentId ? " for this environment" : ""}. Add conditions to the “unused” ones or disable them.
              </p>
            )}
            </>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-auto border-l border-cloverhound-gray-200 pl-3 dark:border-cloverhound-gray-850">
          {selected ? (
            <MockEditor key={selected.id} project={project} request={request} mock={selected} environments={environments} />
          ) : (
            <p className="pt-8 text-center text-small text-cloverhound-gray-600">Select or add a variant.</p>
          )}
        </div>
      </div>
    </div>
  )
}

function MockUrlHint({
  project,
  request,
  environments,
  serviceSlug,
  environmentId,
  mockToken,
  host,
}: {
  project: Project
  request: ApiRequest
  environments: Environment[]
  serviceSlug: string | null
  environmentId: number | null
  mockToken?: string
  host: string
}) {
  const token = mockToken ?? project.mock_token
  if (!token) return null

  // Environment segment follows the builder's env switcher; blank = the
  // env-agnostic URL.
  const envSlug = environments.find((e) => e.id === environmentId)?.slug ?? null
  const path = request.url.replace(/^https?:\/\/[^/]+/, "").replace(/^\/+/, "")

  const segments = ["m", token]
  if (envSlug) segments.push(envSlug)
  if (serviceSlug) segments.push(serviceSlug)
  const url = `${host}/${segments.join("/")}/${path}`

  return (
    <div className="flex w-full items-center gap-2">
      <span className="shrink-0 text-mini font-medium text-cloverhound-gray-600">Mock URL</span>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(url)
          toast.success("Mock URL copied")
        }}
        className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-cloverhound-gray-250 bg-cloverhound-gray-50 px-2.5 py-1.5 text-left hover:bg-cloverhound-gray-100 dark:border-cloverhound-gray-800 dark:bg-cloverhound-gray-900 dark:hover:bg-cloverhound-gray-850"
        title={`Copy: ${url}`}
      >
        <span className="min-w-0 flex-1 truncate font-mono text-micro text-cloverhound-gray-700 dark:text-cloverhound-gray-300">
          {url}
        </span>
        <Copy className="size-3.5 shrink-0 text-cloverhound-gray-500" aria-hidden="true" />
      </button>
    </div>
  )
}

function MockEditor({
  project,
  request,
  mock,
  environments,
}: {
  project: Project
  request: ApiRequest
  mock: Mock
  environments: Environment[]
}) {
  const [draft, setDraft] = React.useState<Mock>(mock)
  const [saving, setSaving] = React.useState(false)
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Editable response headers as rows (the model stores them as an object).
  const [headerEntries, setHeaderEntries] = React.useState<KeyValueEntry[]>(() =>
    Object.entries(mock.response_headers ?? {}).map(([key, value]) => ({ key, value, enabled: true })),
  )
  // Body highlighting/format isn't persisted (Content-Type carries the truth),
  // so infer it from the declared Content-Type header, else sniff the body.
  const [bodyFormat, setBodyFormat] = React.useState<"json" | "xml" | "text">(() => inferBodyFormat(mock))

  // Reset the draft only when a DIFFERENT variant is selected — not when our
  // own debounced autosave reloads props (which would clobber in-flight edits).
  React.useEffect(() => setDraft(mock), [mock.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // A null body means "no body" (e.g. 204 No Content); "" is an empty body.
  const hasBody = draft.response_body !== null

  function updateHeaders(entries: KeyValueEntry[]) {
    setHeaderEntries(entries)
    const object = Object.fromEntries(
      entries.filter((e) => e.enabled && e.key.trim()).map((e) => [e.key.trim(), e.value]),
    )
    update({ response_headers: object })
  }

  function changeBodyMode(mode: string) {
    if (mode === "none") {
      update({ response_body: null })
    } else {
      setBodyFormat(mode as "json" | "xml" | "text")
      if (draft.response_body === null) update({ response_body: "" })
    }
  }

  function persist(next: Mock) {
    setSaving(true)
    router.patch(
      `/projects/${project.slug}/requests/${request.id}/mocks/${next.id}`,
      {
        name: next.name,
        enabled: next.enabled,
        environment_id: next.environment_id,
        conditions: next.conditions,
        response_status: next.response_status,
        response_headers: next.response_headers,
        response_body: next.response_body,
        delay_ms: next.delay_ms,
        failure_mode: next.failure_mode,
        emit_content_type: next.emit_content_type,
        emit_content_length: next.emit_content_length,
        state_updates: next.state_updates,
      } as unknown as Record<string, never>,
      { preserveScroll: true, preserveState: true, only: ["selected_request"], onFinish: () => setSaving(false) },
    )
  }

  function update(patch: Partial<Mock>) {
    setDraft((prev) => {
      const next = { ...prev, ...patch }
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => persist(next), 700)
      return next
    })
  }

  function deleteMock() {
    if (!confirm(`Delete mock "${draft.name}"?`)) return
    router.delete(`/projects/${project.slug}/requests/${request.id}/mocks/${draft.id}`, {
      preserveScroll: true,
      only: ["selected_request"],
    })
  }

  const serviceEnvs = environments.filter((e) => e.folder_id !== null)

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div className="flex items-center gap-2">
        <Input
          value={draft.name}
          onChange={(e) => update({ name: e.target.value })}
          className="max-w-48 font-semibold"
          aria-label="Mock name"
        />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-micro text-cloverhound-gray-500">{saving ? "Saving…" : "Enabled"}</span>
          <Switch checked={draft.enabled} onCheckedChange={(v) => update({ enabled: v })} aria-label="Enabled" />
          <Button variant="ghost" size="iconXs" onClick={deleteMock} aria-label="Delete mock">
            <Trash2 className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <LabeledField label="Status">
          <Input
            value={String(draft.response_status)}
            onChange={(e) => update({ response_status: Number(e.target.value.replace(/\D/g, "")) || 200 })}
            className="font-mono"
            size="sm"
          />
        </LabeledField>
        <LabeledField label="Delay (ms)">
          <Input
            value={String(draft.delay_ms)}
            onChange={(e) => update({ delay_ms: Math.min(30000, Number(e.target.value.replace(/\D/g, "")) || 0) })}
            className="font-mono"
            size="sm"
          />
        </LabeledField>
        <LabeledField label="Environment">
          <Select.Root
            value={draft.environment_id ? String(draft.environment_id) : "all"}
            onValueChange={(v) => update({ environment_id: v === "all" || !v ? null : Number(v) })}
          >
            <Select.Trigger aria-label="Environment restriction" className="h-8">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All environments</Select.Item>
              {serviceEnvs.map((e) => (
                <Select.Item key={e.id} value={String(e.id)}>
                  {e.name} only
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </LabeledField>
      </div>

      <LabeledField label="Conditions (all must match; none = default/fallback)">
        <ConditionsEditor
          conditions={draft.conditions}
          onChange={(conditions) => update({ conditions })}
        />
      </LabeledField>

      <LabeledField label="Response headers">
        <KeyValueEditor
          entries={headerEntries}
          onChange={updateHeaders}
          keyPlaceholder="Header"
          valuePlaceholder="Value"
          addLabel="Add header"
        />
      </LabeledField>

      <LabeledField label="Response body">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Select.Root value={hasBody ? bodyFormat : "none"} onValueChange={(v) => v && changeBodyMode(v)}>
              <Select.Trigger aria-label="Response body type" className="h-8 w-40">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {BODY_MODES.map((m) => (
                  <Select.Item key={m.value} value={m.value}>
                    {m.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>

            <label
              className={cn(
                "flex items-center gap-1.5 text-mini text-cloverhound-gray-700 dark:text-cloverhound-gray-300",
                !hasBody && "opacity-50",
              )}
            >
              <Checkbox
                checked={draft.emit_content_type}
                onCheckedChange={(c) => update({ emit_content_type: c === true })}
                disabled={!hasBody}
                aria-label="Send default Content-Type"
              />
              Send default Content-Type{hasBody ? ` (${CONTENT_TYPE_FOR[bodyFormat]})` : ""}
            </label>
          </div>

          {hasBody ? (
            <>
              <CodeEditor
                value={draft.response_body ?? ""}
                onChange={(response_body) => update({ response_body })}
                language={bodyFormat}
                placeholder={BODY_PLACEHOLDERS[bodyFormat]}
                minHeight="160px"
              />
              <p className="text-micro text-cloverhound-gray-500">
                Liquid template. Context: <code>params</code>, <code>query</code>, <code>headers</code>,{" "}
                <code>json</code>, <code>env</code>, <code>now_iso</code>. Filters: <code>uuid</code>,{" "}
                <code>random_int</code>.
              </p>
            </>
          ) : (
            <p className="rounded-md bg-cloverhound-gray-50 px-2.5 py-2 text-mini text-cloverhound-gray-600 dark:bg-cloverhound-gray-900">
              No response body will be sent — useful for <code>204 No Content</code> or redirects. Only the
              status and headers above are returned.
            </p>
          )}
        </div>
      </LabeledField>
    </div>
  )
}

function ConditionsEditor({
  conditions,
  onChange,
}: {
  conditions: MockCondition[]
  onChange: (conditions: MockCondition[]) => void
}) {
  function update(index: number, patch: Partial<MockCondition>) {
    onChange(conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)))
  }

  return (
    <div className="flex flex-col gap-1.5">
      {conditions.map((condition, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Select.Root value={condition.source} onValueChange={(v) => v && update(i, { source: v as MockCondition["source"] })}>
            <Select.Trigger className="h-8 w-28" aria-label="Source">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {CONDITION_SOURCES.map((s) => (
                <Select.Item key={s} value={s}>
                  {s}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          <Input
            size="sm"
            value={condition.key}
            onChange={(e) => update(i, { key: e.target.value })}
            placeholder="key"
            className="w-28 font-mono"
          />
          <Select.Root value={condition.operator} onValueChange={(v) => v && update(i, { operator: v as MockCondition["operator"] })}>
            <Select.Trigger className="h-8 w-24" aria-label="Operator">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {CONDITION_OPERATORS.map((o) => (
                <Select.Item key={o} value={o}>
                  {o}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {condition.operator !== "exists" && (
            <Input
              size="sm"
              value={condition.value ?? ""}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="value"
              className="flex-1 font-mono"
            />
          )}
          <Button
            variant="ghost"
            size="iconXs"
            onClick={() => onChange(conditions.filter((_, idx) => idx !== i))}
            aria-label="Remove condition"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      ))}
      <div>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => onChange([...conditions, { source: "query", key: "", operator: "eq", value: "" }])}
        >
          <Plus className="size-3.5" aria-hidden="true" />
          Add condition
        </Button>
      </div>
    </div>
  )
}

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-mini font-medium text-cloverhound-gray-700 dark:text-cloverhound-gray-300">{label}</span>
      {children}
    </div>
  )
}
